import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React from 'react';

import AspectRatio from 'components/AspectRatio';
import TertiaryHeading from 'components/Headings/TertiaryHeading';
import Image from 'components/ImageV2';
import Paragraph from 'components/Paragraph';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

const ArticleCard = ({ header, body, linkTo, image, date }) => {
  const imageWidth = image.file.details.image.width;
  const imageHeight = image.file.details.image.height;
  const formattedDate = date ? dayjs(date).format('MMMM D, YYYY') : null;

  return (
    <div className="ArticleCard">
      <a
        className="ArticleCard__image-link"
        href={linkTo}
        aria-label={I18n.localize('articleCard.ariaLabel.link')}
      >
        <AspectRatio width={imageWidth} height={imageHeight}>
          <Image coverParentNode width={imageWidth} height={imageHeight} src={image.file.url} />
        </AspectRatio>
      </a>

      <div className="ArticleCard__content">
        <a
          className="ArticleCard__content-link"
          href={linkTo}
          aria-label={I18n.localize('articleCard.ariaLabel.link')}
        >
          <TertiaryHeading className="ArticleCard__header">{header}</TertiaryHeading>

          {date && <span className="ArticleCard__date">{formattedDate}</span>}
        </a>
        <Paragraph className="ArticleCard__body">{body}</Paragraph>
      </div>

      <style jsx>
        {`
          .ArticleCard__image-link {
            margin: 0;
            padding: 0;
            text-decoration: none;
            display: block;
            margin-bottom: ${Theme.get('spacing') * 2}px;
            cursor: pointer;
          }

          .ArticleCard__content-link {
            margin: 0;
            padding: 0;
            text-decoration: none;
            display: block;
            cursor: pointer;
          }

          .ArticleCard__date {
            display: block;
            margin-bottom: ${Theme.get('spacing')}px;
            color: ${Theme.get('colors-black-40')};
            font-family: ${Theme.get('font-family')};
            font-size: ${Theme.get('font-size-small-px')};
            letter-spacing: ${Theme.get('letter-spacing-extra-large-px')};
            font-weight: ${Theme.get('font-weight-bold')};
            line-height: ${Theme.get('spacing') * 3.5}px;
            text-transform: uppercase;
          }
        `}
      </style>

      <style jsx global>
        {`
          .ArticleCard__header.typography--tertiary-heading {
            margin-bottom: ${Theme.get('spacing')}px;
          }

          .ArticleCard__body.Paragraph {
            font-size: ${Theme.get('font-size-description-px')};
            margin: 0;
          }

          .ArticleCard__image-link img {
            width: 100%;
            height: 100%;
            max-width: 100%;
          }
        `}
      </style>
    </div>
  );
};

ArticleCard.propTypes = {
  header: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  linkTo: PropTypes.string.isRequired,
  image: PropTypes.shape({
    file: PropTypes.shape({
      url: PropTypes.string,
      details: PropTypes.shape({
        image: PropTypes.shape({
          width: PropTypes.number,
          height: PropTypes.number,
        }),
      }),
    }),
  }).isRequired,
  date: PropTypes.string,
};

ArticleCard.defaultProps = {
  date: '',
};

export default ArticleCard;
